/**
 * La planificación laboral (TH-2), como la devuelve el servidor. Los montos viajan como
 * TEXTO —son `Decimal`— y así se pintan, con `monto()`: convertirlos a `number` para
 * mostrarlos es perder céntimos.
 */

export type EstadoPeriodo = "POR_NACER" | "PENDIENTE" | "PROGRAMADO" | "DISFRUTADO"

export type CalculoVacaciones = {
  dias_habiles: number
  dias_descanso: number
  dias_bono: number
  salario_mensual: string
  salario_diario: string
  monto_habiles: string
  monto_descanso: string
  monto_bono: string
  total: string
}

export type VacacionProgramada = CalculoVacaciones & {
  id: number
  persona_id: number
  periodo: number
  inicio: string
  ultimo_dia: string
  reintegro: string
  estado: "PROGRAMADA" | "ANULADA"
  documento_th_id: number | null
}

export type Periodo = {
  periodo: number
  texto: string
  nace: string
  dias_habiles: number
  dias_bono: number
  estado: EstadoPeriodo
  programacion: VacacionProgramada | null
}

export type UtilidadProgramada = {
  id: number
  persona_id: number
  anio: number
  fecha_pago: string
  dias_ejercicio: number
  meses: number
  dias: string
  salario_diario: string
  total: string
  estado: "PROGRAMADA" | "ANULADA"
  documento_th_id: number | null
}

export type EstadoUtilidades = "PENDIENTE" | "PROGRAMADA" | "PAGADA"

export type Utilidades = {
  anio: number
  dias_ejercicio: number
  dias_fijados: boolean
  meses: number
  dias: string
  monto: string | null
  estado: EstadoUtilidades
  programacion: UtilidadProgramada | null
}

export const ESTADO_UTILIDADES: Record<
  EstadoUtilidades,
  { texto: string; tono: "aviso" | "info" | "exito" }
> = {
  PENDIENTE: { texto: "Sin programar", tono: "aviso" },
  PROGRAMADA: { texto: "Programada", tono: "info" },
  PAGADA: { texto: "Pagada", tono: "exito" },
}

/** Se anula lo programado cuya fecha de pago no ha llegado (el servidor lo comprueba). */
export function sePuedeAnularUtilidades(u: Utilidades, hoy: string): boolean {
  return u.estado === "PROGRAMADA" && u.programacion !== null && u.programacion.fecha_pago > hoy
}

/** Una persona en «programar a todo el personal»: programada, o por qué no. */
export type ResultadoProgramacion = {
  persona_id: number
  persona: string
  programada_id: number | null
  motivo: string | null
}

export type Garantia = {
  meses: number
  anios: number
  dias_trimestrales: number
  dias_adicionales: number
  dias_garantia: number
  dias_retroactivo: number
  integral_diario: string | null
  monto_garantia: string | null
  monto_retroactivo: string | null
  monto_mayor: string | null
}

export type Planificacion = {
  persona_id: number
  fecha_ingreso: string | null
  antiguedad: string
  salario_mensual: string | null
  salario_diario: string | null
  periodos: Periodo[]
  utilidades: Utilidades[]
  garantia: Garantia | null
  avisos: string[]
}

export type Propuesta = {
  periodo: number
  periodo_texto: string
  inicio: string
  ultimo_dia: string
  reintegro: string
  calculo: CalculoVacaciones
}

export type Evento = {
  fecha: string
  tipo:
    | "VACACIONES_NACE"
    | "VACACIONES_INICIO"
    | "VACACIONES_REINTEGRO"
    | "GARANTIA"
    | "UTILIDADES"
    | "UTILIDADES_PAGO"
  persona_id: number
  persona: string
  titulo: string
}

export const ESTADO_PERIODO: Record<
  EstadoPeriodo,
  { texto: string; tono: "neutro" | "aviso" | "info" | "exito" }
> = {
  POR_NACER: { texto: "Por nacer", tono: "neutro" },
  PENDIENTE: { texto: "Pendiente", tono: "aviso" },
  PROGRAMADO: { texto: "Programado", tono: "info" },
  DISFRUTADO: { texto: "Disfrutado", tono: "exito" },
}

/** Lo que se ofrece en cada período: sólo se programa lo nacido y sin programar. */
export function sePuedeProgramar(p: Periodo): boolean {
  return p.estado === "PENDIENTE"
}

/** Se anula lo programado que aún no empezó (el servidor lo vuelve a comprobar). */
export function sePuedeAnular(p: Periodo, hoy: string): boolean {
  return p.estado === "PROGRAMADO" && p.programacion !== null && p.programacion.inicio > hoy
}
